"use client";
import { apiAddComment } from "@/apis/offers";
import { emptyStar, filledStar, FontAwesomeIcon } from "@/constants/icons";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

interface Props {
    offer_id:   string;
    company_id: string;
}

const LABELS = ["", "Poor", "Fair", "Good", "Great", "Excellent"];

const RateUs = ({ offer_id, company_id }: Props) => {
    const [rating,   setRating]   = useState(0);
    const [hovered,  setHovered]  = useState(0);
    const [comment,  setComment]  = useState("");
    const [loading,  setLoading]  = useState(false);
    const [hasRated, setHasRated] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        const last = localStorage.getItem(`hasRated_${offer_id}`);
        if (last && Date.now() - parseInt(last) < 86400000) setHasRated(true);
    }, [offer_id]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (hasRated) {
            toast.error("Already rated, try again later!", { autoClose: 2000 });
            return;
        }
        if (!rating && !comment.trim()) {
            toast.error("Please select a rating or add a comment.", { autoClose: 2000 });
            return;
        }
        setLoading(true);
        try {
            const res = await apiAddComment(offer_id, company_id, comment, rating.toString());
            if (res.status === "success") {
                toast.success("Thanks for your feedback!", { autoClose: 2000 });
                localStorage.setItem(`hasRated_${offer_id}`, Date.now().toString());
                setHasRated(true);
                setSubmitted(true);
                setRating(0);
                setComment("");
            } else {
                toast.error("Something went wrong. Try again!", { autoClose: 2000 });
            }
        } catch {
            toast.error("Failed to submit feedback.", { autoClose: 2000 });
        } finally {
            setLoading(false);
        }
    };

    const active = hovered || rating;

    /* ── Success state ── */
    if (submitted) {
        return (
            <div className="flex flex-col items-center justify-center gap-3 py-8 text-center">
                <div className="w-12 h-12 rounded-2xl bg-[#D61C4E]/8 border border-[#D61C4E]/20
                                flex items-center justify-center">
                    <svg className="w-5 h-5 text-[#D61C4E]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                </div>
                <p className="text-[13px] font-black text-slate-800 uppercase tracking-wider">
                    Thanks for rating!
                </p>
                <p className="text-[11px] text-slate-400 font-medium">
                    Your feedback helps other shoppers.
                </p>
            </div>
        );
    }

    return (
        <div className="w-full flex flex-col gap-5">
            {/* Header */}
            <div className="flex items-center gap-2">
                <span className="w-4 h-[2px] bg-[#D61C4E] rounded-full" />
                <h4 className="text-[10px] font-black uppercase tracking-[0.25em] text-[#D61C4E]">
                    Rate This Deal
                </h4>
            </div>

            <form onSubmit={handleSubmit} autoComplete="off" className="flex flex-col gap-4">

                {/* Stars */}
                <div className="flex flex-col gap-2.5 p-4 rounded-xl bg-slate-50 border border-slate-100">
                    <div className="flex items-center gap-1.5">
                        {[...Array(5)].map((_, i) => (
                            <button
                                type="button"
                                key={i}
                                onClick={() => setRating(i + 1)}
                                onMouseEnter={() => setHovered(i + 1)}
                                onMouseLeave={() => setHovered(0)}
                                className="transition-transform active:scale-90 hover:scale-110
                                           outline-none border-none bg-transparent p-0"
                            >
                                <FontAwesomeIcon
                                    icon={i < active ? filledStar : emptyStar}
                                    className={`w-6 h-6 transition-all duration-150
                                               ${i < active
                                                   ? "text-amber-400 drop-shadow-sm"
                                                   : "text-slate-200"
                                               }`}
                                />
                            </button>
                        ))}
                    </div>

                    {/* Label */}
                    <div className="flex items-center gap-2 h-4">
                        {active > 0 ? (
                            <>
                                <span className="text-[9px] font-black uppercase tracking-[0.2em] text-amber-500">
                                    {LABELS[active]}
                                </span>
                                <div className="flex gap-0.5">
                                    {[...Array(active)].map((_, i) => (
                                        <span key={i} className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                                    ))}
                                </div>
                            </>
                        ) : (
                            <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400">
                                Tap to rate
                            </span>
                        )}
                    </div>
                </div>

                {/* Comment */}
                <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">
                        Your Comment
                    </label>
                    <textarea
                        rows={3}
                        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl
                                   text-[13px] text-slate-700 font-medium resize-none
                                   focus:ring-4 focus:ring-[#D61C4E]/8 focus:border-[#D61C4E]/40
                                   outline-none transition-all placeholder:text-slate-300"
                        placeholder="Share your experience..."
                        value={comment}
                        onChange={e => setComment(e.target.value)}
                    />
                </div>

                {/* Submit */}
                <button
                    type="submit"
                    disabled={loading || hasRated}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-xl
                               bg-[#D61C4E] hover:bg-slate-900 text-white
                               font-black text-[10px] uppercase tracking-widest
                               shadow-md shadow-[#D61C4E]/20 transition-all duration-300
                               active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {loading ? (
                        <div className="flex gap-1">
                            {[0,1,2].map(i => (
                                <div key={i} className="w-1.5 h-1.5 rounded-full bg-white animate-bounce"
                                    style={{ animationDelay: `${i * 0.15}s` }} />
                            ))}
                        </div>
                    ) : hasRated ? "Already Rated" : "Submit Feedback"}
                </button>
            </form>
        </div>
    );
};

export default RateUs;
